import { api } from "../lib/api";
import { session } from "../stores/session";

export type SignConvention = "negative_is_debit" | "separate_columns" | "type_column";
export type StatementImportStatus = "pending_review" | "committed" | "discarded";
export type JobStatus = "queued" | "running" | "done" | "failed";

export interface ImportProfile {
  id: string;
  name: string;
  bank_hint: string | null;
  column_map: Record<string, unknown>;
  date_format: string;
  decimal_style: string;
  header_rows: number;
  sign_convention: SignConvention;
  invert_amount: boolean;
  created_at: string;
  updated_at: string;
}

export interface BankAccount {
  id: string;
  company_id: string;
  name: string;
  bank_name: string | null;
  currency: string;
  sort_code: string | null;
  account_number: string | null;
  import_profile_id: string | null;
  import_profile_name: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BankAccountInput {
  name: string;
  bank_name?: string | null;
  currency?: string;
  sort_code?: string | null;
  account_number?: string | null;
  import_profile_id?: string | null;
}

export interface PreviewRow {
  row_number: number;
  date: string | null;
  description: string;
  amount: string | null;
  balance: string | null;
  reference: string | null;
  errors: string[];
  is_duplicate: boolean;
  /** Balance sanity check (06-banking-yearend.md) — set only when this row's balance
   * doesn't follow from the previous row's; advisory, never blocks commit. */
  balance_warning: string | null;
}

export interface ImportPreview {
  statement_import_id: string;
  row_count: number;
  valid_count: number;
  error_count: number;
  duplicate_count: number;
  rows: PreviewRow[];
  /** Set when this statement's implied starting balance doesn't match the account's
   * last known balance — a possible gap of un-imported transactions between the two. */
  gap_warning: string | null;
}

export interface ImportProfileInput {
  name: string;
  bank_hint?: string | null;
  column_map: Record<string, unknown>;
  date_format: string;
  decimal_style: string;
  header_rows: number;
  sign_convention: SignConvention;
  invert_amount: boolean;
}

export interface Job {
  id: string;
  company_id: string;
  kind: string;
  status: JobStatus;
  progress: number;
  error: string | null;
  payload: Record<string, unknown> | null;
}

export interface StatementImport {
  id: string;
  bank_account_id: string;
  filename: string;
  row_count: number;
  imported_count: number;
  duplicate_count: number;
  status: StatementImportStatus;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  updated_at: string;
}

export function listImportProfiles(): Promise<ImportProfile[]> {
  return api.get<ImportProfile[]>("/api/import-profiles");
}

export function getImportProfile(id: string): Promise<ImportProfile> {
  return api.get<ImportProfile>(`/api/import-profiles/${id}`);
}

export function createImportProfile(data: ImportProfileInput): Promise<ImportProfile> {
  return api.post<ImportProfile>("/api/import-profiles", data);
}

export function updateImportProfile(
  id: string,
  data: ImportProfileInput & { updated_at: string },
): Promise<ImportProfile> {
  return api.put<ImportProfile>(`/api/import-profiles/${id}`, data);
}

export function deleteImportProfile(id: string): Promise<void> {
  return api.delete(`/api/import-profiles/${id}`);
}

export function previewImportProfileMapping(
  profile: ImportProfileInput,
  csvSample: string,
): Promise<PreviewRow[]> {
  return api.post<PreviewRow[]>("/api/import-profiles/preview", {
    profile,
    csv_sample: csvSample,
  });
}

export function listBankAccounts(): Promise<BankAccount[]> {
  return api.get<BankAccount[]>("/api/bank-accounts");
}

export function getBankAccount(id: string): Promise<BankAccount> {
  return api.get<BankAccount>(`/api/bank-accounts/${id}`);
}

export function createBankAccount(data: BankAccountInput): Promise<BankAccount> {
  return api.post<BankAccount>("/api/bank-accounts", data);
}

export function updateBankAccount(
  id: string,
  data: BankAccountInput & { updated_at: string },
): Promise<BankAccount> {
  return api.put<BankAccount>(`/api/bank-accounts/${id}`, data);
}

export function archiveBankAccount(id: string): Promise<void> {
  return api.delete(`/api/bank-accounts/${id}`);
}

export function uploadStatement(accountId: string, file: File): Promise<ImportPreview> {
  return api.upload<ImportPreview>(`/api/bank-accounts/${accountId}/imports`, file);
}

export function commitImport(statementImportId: string): Promise<Job> {
  return api.post<Job>(`/api/imports/${statementImportId}/commit`);
}

export function discardImport(statementImportId: string): Promise<void> {
  return api.post(`/api/imports/${statementImportId}/discard`);
}

export function listStatementImports(
  accountId: string,
  page = 1,
  pageSize = 20,
): Promise<{ items: StatementImport[]; total: number }> {
  return api.getPaged<StatementImport>(
    `/api/bank-accounts/${accountId}/imports?page=${String(page)}&page_size=${String(pageSize)}`,
  );
}

/** Direct browser navigation (the download link's href), same "?company_id instead of
 * the header a plain GET can't attach" pattern as invoicePdfUrl (03-api-spec.md). */
export function statementImportDownloadUrl(statementImportId: string): string {
  const params = new URLSearchParams({ company_id: session.companyId.value ?? "" });
  return `/api/imports/${statementImportId}/download?${params.toString()}`;
}
